import { css, html, nothing } from 'lit';
import { t } from '../i18n';
import '../elements/vitesse-icon';
import '../elements/vitesse-pill-tabs';
/**
 * The `.base-status-badge` rules as a shadow-DOM-consumable `css` — the exact
 * mirror of `styles/components/base-status-badge.scss` (the light-DOM copy).
 * A shadow-DOM Lit element that renders `statusBadge()` must include this in its
 * `static styles` (global SCSS doesn't cross the shadow boundary), the same dual
 * light-SCSS / shadow-css pattern the icon-button uses. Colours read the theme's
 * CSS custom properties, which inherit through the shadow boundary.
 */
export const statusBadgeStyles = css `
  .base-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 5px;
    font-weight: 400;
  }
  .base-status-badge-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .base-status-badge-label {
    white-space: nowrap;
  }
  .base-status-badge-green {
    background-color: rgba(0, 186, 124, 0.15);
    color: var(--green);
  }
  .base-status-badge-yellow {
    background-color: rgba(226, 177, 60, 0.15);
    color: var(--yellow);
  }
  .base-status-badge-gray {
    background-color: rgba(160, 160, 160, 0.1);
    color: var(--text-secondary);
  }
  .base-status-badge-blue {
    background-color: rgba(26, 140, 216, 0.1);
    color: var(--blue, #1a8cd8);
  }
  .base-status-badge-red {
    background-color: rgba(224, 62, 66, 0.15);
    color: var(--red);
  }
  .base-status-badge-purple {
    background-color: rgba(139, 92, 246, 0.15);
    color: var(--purple, #8b5cf6);
  }
  .base-status-badge-orange {
    background-color: rgba(255, 152, 0, 0.15);
    color: var(--orange, #ff9800);
  }
`;
export const ENTITY_ICON = {
    gateway: 'hard-drive',
    controller: 'cpu',
    model: 'git-compare-arrows',
    integration: 'database-arrow-up',
    camera: 'camera',
    dashboard: 'bar-chart-3',
};
/** Maps a backend status string to the agent's badge color (BaseStatusBadge). */
export function statusColor(status) {
    switch (status) {
        case 'connected':
        case 'running':
        case 'recording':
            return 'green';
        case 'connecting':
        case 'starting':
        case 'reconnecting':
        case 'discovering':
        case 'waiting':
            return 'yellow';
        case 'error':
        case 'stopping':
        // An integration whose credentials the remote system rejected. Red like
        // 'error' — data is not flowing — but its own word, because the fix is
        // different: a password, not a cable. (The service stops retrying it, so it
        // will not turn green on its own the way 'connecting' does.)
        case 'unauthorized':
        // Red, per the fleet-status convention: red = offline, yellow = connecting,
        // green = connected. An operator scanning a fleet wall needs "this box is not
        // there" to be as loud as an error, because operationally it is the same
        // problem — the data isn't flowing. (It stays a distinct WORD from 'error' so
        // the cause is still legible; only the urgency is shared.)
        case 'offline':
            return 'red';
        // 'disconnected' lands here: the USER detached this frontend, so it is a choice,
        // not a fault, and must not shout. That is the one gray state worth keeping.
        default:
            return 'gray';
    }
}
// BaseStatusBadge markup. Optional leading `icon` (a vitesse-icon name) renders in
// the `base-status-badge-icon` slot — used by the gateway network-IP badges
// (ethernet-port / factory / wifi / globe-lock). Existing 2-arg calls unaffected.
// The label may be a template rather than a string: a badge whose unit word drops at a narrow
// width needs the number and the word in separate spans, and building that around the badge instead
// of inside it would be a second spelling of a shared primitive.
export function statusBadge(color, label, icon) {
    return html `<span class="base-status-badge base-status-badge-${color}"
    >${icon ? html `<span class="base-status-badge-icon"><vitesse-icon name=${icon} size="12"></vitesse-icon></span>` : nothing}<span
      class="base-status-badge-label"
      >${label}</span
    ></span
  >`;
}
// Symbol-discovery (catalog) lifecycle — shown as its own badge NEXT TO the
// connection status so the evolution is visible (idle → scanning → ready). This
// is the ControllerDescriptor.discovery_status field, independent of the
// connection `status`.
const DISCOVERY_BADGE = {
    idle: { color: 'gray', key: 'discovery.idle' },
    discovering: { color: 'yellow', key: 'discovery.discovering' }, // scanning the PLC
    running: { color: 'green', key: 'discovery.running' }, // green/healthy — catalog ready
    error: { color: 'red', key: 'discovery.error' },
};
export function discoveryBadge(discoveryStatus) {
    const d = DISCOVERY_BADGE[discoveryStatus] ?? DISCOVERY_BADGE.idle;
    return statusBadge(d.color, capitalize(t(d.key)));
}
/** Capitalize the first letter — status-word badges read "Connected", not
 *  "connected". Targeted to statusText + discoveryBadge so it never touches the
 *  RTT/count badges ("12ms", "5000 symbols"); locale-safe (no-op on scripts
 *  without case, e.g. Japanese). */
function capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
// Translated controller connection-status word ('connected' → 'conectado', …).
// Kept here (shared) because home + gateway/controller detail all badge status.
const STATUS_KEYS = {
    connected: 'status.connected',
    connecting: 'status.connecting',
    reconnecting: 'status.reconnecting',
    disconnected: 'status.disconnected',
    // A box that is powered off / unreachable. DISTINCT from 'disconnected', which
    // means the USER disconnected — conflating them would report an intent nobody
    // expressed. Absent from this map, 'offline' fell through to status.error and
    // rendered a red "Error", which is why it needs a key and not just a caller.
    offline: 'status.offline',
    discovered: 'status.discovered',
    error: 'status.error',
    unauthorized: 'status.unauthorized',
};
export function statusText(status) {
    return capitalize(t(STATUS_KEYS[status] ?? 'status.error'));
}
// BaseOverviewCard markup.
export function overviewCard(o) {
    const onKey = o.clickable
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                o.onClick?.();
            }
        }
        : undefined;
    return html `
    <div
      class="base-overview-card ${o.clickable ? 'clickable' : ''}"
      role=${o.clickable ? 'button' : nothing}
      tabindex=${o.clickable ? '0' : nothing}
      @click=${o.clickable ? o.onClick : undefined}
      @keydown=${onKey}
      @mouseenter=${o.onHover}
      @mouseleave=${o.onHoverEnd}
    >
      <h3 class="base-overview-card-title">
        ${o.icon || o.entityType
        ? html `<span class="base-overview-card-title-icon"
                ><vitesse-icon name=${o.icon ?? ENTITY_ICON[o.entityType]} size="16"></vitesse-icon
              ></span>`
        : nothing}
        <span class="base-overview-card-title-text" title=${o.title}>${o.title}</span>
      </h3>
      ${o.description ? html `<p class="base-overview-card-description">${o.description}</p>` : nothing}
      ${o.badges
        ? html `<div class="base-overview-card-status-actions-row">
              <div class="base-overview-card-status-actions-left">${o.badges}</div>
            </div>`
        : nothing}
      ${o.preview ? html `<div class="base-overview-card-preview">${o.preview}</div>` : nothing}
      ${o.actions ? html `<div class="base-overview-card-actions">${o.actions}</div>` : nothing}
    </div>
  `;
}
/** A titled content panel — the app-wide "section card": a bordered surface with an (optional icon +)
 *  title, an optional sub, optional right-aligned header actions, and a body. THE reusable section
 *  chrome for config/detail pages, so no app hand-rolls panel CSS. */
export function panel(o) {
    return html `<section class="base-panel">
    <div class="base-panel-head">
      ${o.icon ? html `<span class="base-panel-icon"><vitesse-icon name=${o.icon} size="17"></vitesse-icon></span>` : nothing}
      <h3 class="base-panel-title">${o.title}</h3>
      <!-- A badge that qualifies the TITLE rides beside it, not out in the actions: "Super-Admin"
           describes what this panel is, and pushed to the far right it reads as a control. -->
      ${o.titleBadge ? html `<span class="base-panel-title-badge">${o.titleBadge}</span>` : nothing}
      ${o.actions ? html `<div class="base-panel-actions">${o.actions}</div>` : nothing}
    </div>
    ${o.sub ? html `<p class="base-panel-sub">${o.sub}</p>` : nothing}
    <div class="base-panel-body">${o.body}</div>
  </section>`;
}
/** One labelled form row — label (+ optional badge / hint) beside its control (inline), or above it
 *  (`stacked`, for wide controls). The reusable row primitive for the config forms; consecutive rows
 *  divide automatically. */
export function field(o) {
    return html `<div class="base-field ${o.stacked ? 'base-field-stack' : ''}">
    <div class="base-field-label">
      <span class="base-field-namerow"><span class="base-field-name">${o.label}</span>${o.badge ?? nothing}</span>
      ${o.hint ? html `<span class="base-field-hint">${o.hint}</span>` : nothing}
    </div>
    <div class="base-field-control">${o.control}</div>
  </div>`;
}
/** One item inside a `panel()` list (a specialist, a provider, an MCP server): a divided block with a
 *  title (+ optional sub), optional right-aligned actions, and a body of `field()` rows. */
export function panelItem(o) {
    return html `<div class="base-panel-item">
    <div class="base-panel-item-head">
      <div class="base-panel-item-titles">
        <span class="base-panel-item-title">${o.title}</span>${o.sub ? html `<span class="base-panel-item-sub">${o.sub}</span>` : nothing}
      </div>
      ${o.actions ? html `<div class="base-panel-item-actions">${o.actions}</div>` : nothing}
    </div>
    <div class="base-panel-item-body">${o.body}</div>
  </div>`;
}
/** A compact, clickable list row — the summary line for an editable item (a specialist, a provider,
 *  an MCP server) whose full form lives in a dialog. Title (mono by default) + optional sub on the left
 *  open the editor on click; right-aligned `actions` (edit / remove / a toggle) sit outside the click
 *  target. Rows stack with a divider between them. */
export function listRow(o) {
    // Flex-shrink pinned so a logo never squeezes the title; margin only when present, so a row
    // without a leading element is byte-identical to before.
    const lead = o.leading
        ? html `<span style="display:inline-flex;align-items:center;flex:0 0 auto;margin-right:8px">${o.leading}</span>`
        : nothing;
    const titles = html `<span class="base-list-row-titlerow"
      ><span class="base-list-row-title ${o.mono === false ? '' : 'mono'}">${o.title}</span>${o.badge ?? nothing}</span
    >${o.sub ? html `<span class="base-list-row-sub">${o.sub}</span>` : nothing}`;
    // `stopPropagation` because the box sits inside a row whose whole surface opens the item —
    // without it, ticking a checkbox would also navigate away from the list you are selecting in.
    const box = o.onSelect
        ? html `<input
        type="checkbox"
        class="base-list-row-select"
        aria-label=${o.selectLabel ?? o.title}
        .checked=${!!o.selected}
        @click=${(e) => e.stopPropagation()}
        @change=${(e) => o.onSelect?.(e.target.checked)}
      />`
        : nothing;
    return html `<div
    class="base-list-row ${o.selected ? 'base-list-row--selected' : ''}"
    style=${o.indentPx ? `padding-left:${18 + o.indentPx}px` : ''}
  >
    ${box}${o.control ?? nothing}${o.onOpen
        ? html `<button type="button" class="base-list-row-main" @click=${o.onOpen}>${lead}${titles}</button>`
        : html `<div class="base-list-row-main">${lead}${titles}</div>`}
    ${o.actions ? html `<div class="base-list-row-actions">${o.actions}</div>` : nothing}
  </div>`;
}
/** A small tag/chip for a value in a list (a tool id, a model id). Removable when `onRemove` is given.
 *  The label may be a template for chips that carry a leading glyph (e.g. a series color swatch). */
export function chip(label, onRemove) {
    return html `<span class="base-chip"
    >${label}${onRemove ? html `<button class="base-chip-x" @click=${onRemove}><vitesse-icon name="x" size="11"></vitesse-icon></button>` : nothing}</span
  >`;
}
// BasePillTabBar markup — delegates to <vitesse-pill-tabs>, which adds the
// sliding active indicator (the pills themselves are unchanged markup). The
// className lands on the host element.
export function pillTabBar(opts) {
    return html `<vitesse-pill-tabs
    class=${opts.className ?? ''}
    .tabs=${opts.tabs}
    .active=${opts.active}
    .onChange=${opts.onChange}
  ></vitesse-pill-tabs>`;
}
// BaseGrid markup wrapper.
export function baseGrid(items, columns = 3) {
    return html `<div class="base-grid grid-cols-${columns}">${items}</div>`;
}
//# sourceMappingURL=templates.js.map